import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import "./posting.css";
import "./PopupForm.css";
import { FiFilter, FiChevronDown, FiX } from "react-icons/fi";
import axios from "axios";
import { FaTrashAlt } from "react-icons/fa";
import { useCurrency } from '../currency/currencyContext'; // Add currency context

const PostingEntries = () => {
  const [activePopup, setActivePopup] = useState(null);
  const popupRef = useRef(null);
  const dropdownRef = useRef(null);

  const [selectedInvoices, setSelectedInvoices] = useState({});
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [showInvoiceTable, setShowInvoiceTable] = useState(false);
  const [totalPayment, setTotalPayment] = useState("0.00");
  const [selectedAccount, setSelectedAccount] = useState("");


  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("");

  const [entries, setEntries] = useState([]);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(5);

  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency(); // Add currency context

  const handleDelete = async (entry) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete the payment entry of ${formatPrice(entry.amount)}?`
    );
    if (!confirmDelete) return;

    try {
      const response = await axios.delete(
        `https://backend.hira-tech.co.uk/order/api/customer-payments/${entry.customer_payment_id}/`
      );
      if (response.status === 204) {
        // Remove the deleted entry from the list
        setEntries((prevEntries) =>
          prevEntries.filter(
            (e) => e.customer_payment_id !== entry.customer_payment_id
          )
        );
      } else {
        alert("Failed to delete the entry.");
      }
    } catch (error) {
      console.error("Delete failed:", error);
      alert("An error occurred while deleting the payment.");
    }
  };

  useEffect(() => {
    axios
      .get("https://backend.hira-tech.co.uk/order/api/customer-payments/")
      .then((response) => {
        setEntries(response.data);
      })
      .catch((error) => {
        console.error("Error fetching entries:", error);
      });
  }, []);
  // Fetch customers from API
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(
          "https://backend.hira-tech.co.uk/user/getAllcustomers/"
        );
        if (!response.ok) {
          throw new Error("Failed to fetch customers");
        }
        const data = await response.json();
        setCustomers(data);
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    };
    fetchCustomers();
  }, []);

  // Fetch unpaid invoices for the selected customer
  useEffect(() => {
    const fetchUnpaidInvoices = async () => {
      if (!selectedCustomerId) {
        setInvoices([]);
        return;
      }
      try {
        const response = await fetch(
          `https://backend.hira-tech.co.uk/order/unpaid-invoices/${selectedCustomerId}/`
        );
        if (!response.ok) {
          throw new Error("Failed to fetch unpaid invoices");
        }
        const data = await response.json();
        // Transform API data to match the invoices structure
        const transformedInvoices = data.map((invoice, index) => ({
          id: index + 1, // Temporary ID for frontend (use invoice.invoice_number if needed)
          description: `Order ${invoice.order_details.order_Tracking_Id}`,
          invoiceDate: invoice.issue_date,
          total: invoice.amount,
          remaining: invoice.payment_due,
          invoiceNumber: invoice.invoice_number, // Store invoice number for reference
        }));
        setInvoices(transformedInvoices);
      } catch (error) {
        console.error("Error fetching unpaid invoices:", error);
        setInvoices([]);
      }
    };
    fetchUnpaidInvoices();
  }, [selectedCustomerId]);

  // const entries = filterAccount
  //   ? originalEntries.filter((entry) => entry.account === filterAccount)
  //   : originalEntries;

  const handleSelectAll = (e) => {
    const updatedSelected = {};
    const updatedPayments = {};
    invoices.forEach((invoice) => {
      updatedSelected[invoice.id] = e.target.checked;
      updatedPayments[invoice.id] = e.target.checked
        ? invoice.remaining.toFixed(2)
        : "0.00";
    });
    setSelectedInvoices(updatedSelected);
    setPaymentAmounts(updatedPayments);
    setTotalPayment(
      Object.values(updatedPayments)
        .reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0)
        .toFixed(2)
    );
  };

  const handleCheckboxChange = (id) => {
    const isSelected = !!selectedInvoices[id];
    const updatedSelectedInvoices = {
      ...selectedInvoices,
      [id]: !isSelected,
    };

    setSelectedInvoices(updatedSelectedInvoices);

    // Update payment amounts based on the new selection
    const updatedPaymentAmounts = {
      ...paymentAmounts,
      [id]: !isSelected
        ? invoices.find((inv) => inv.id === id)?.remaining.toFixed(2) || "0.00"
        : "0.00",
    };

    setPaymentAmounts(updatedPaymentAmounts);

    // Update total payment
    const total = Object.values(updatedPaymentAmounts)
      .reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0)
      .toFixed(2);

    setTotalPayment(total);

    // Extract selected invoice numbers
    const selectedIds = Object.keys(updatedSelectedInvoices).filter(
      (key) => updatedSelectedInvoices[key]
    );

    const selectedInvoiceNumbers = invoices
      .filter((inv) => selectedIds.includes(String(inv.id)))
      .map((inv) => inv.invoiceNumber)
      .join(", ");

    setReferenceNumber(selectedInvoiceNumbers);
  };

  const handlePaymentChange = (id, value) => {
    const cleanedValue = value.replace(/[^0-9.]/g, "");
    const newPayments = { ...paymentAmounts, [id]: cleanedValue };
    setPaymentAmounts(newPayments);
    const total = Object.values(newPayments)
      .map((v) => parseFloat(v))
      .filter((v) => !isNaN(v))
      .reduce((sum, v) => sum + v, 0)
      .toFixed(2);
    setTotalPayment(total);
  };

  const handleTotalPaymentChange = (value) => {
    if (!/^\d*\.?\d*$/.test(value) && value !== "") return;
    const validValue = Math.max(0, parseFloat(value) || 0).toFixed(2);
    setTotalPayment(validValue);
    const selectedIds = Object.keys(selectedInvoices).filter(
      (id) => selectedInvoices[id]
    );
    if (selectedIds.length === 0) return;
    const totalRemaining = selectedIds.reduce(
      (sum, id) =>
        sum + (invoices.find((inv) => inv.id === parseInt(id))?.remaining || 0),
      0
    );
    const ratio =
      totalRemaining > 0 ? parseFloat(validValue) / totalRemaining : 0;
    const newPayments = { ...paymentAmounts };
    selectedIds.forEach((id) => {
      const invoice = invoices.find((inv) => inv.id === parseInt(id));
      newPayments[id] = invoice
        ? (invoice.remaining * ratio).toFixed(2)
        : "0.00";
    });
    setPaymentAmounts(newPayments);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        if (activePopup === "dropdown") {
          setActivePopup(null);
        }
      }
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        activePopup !== "dropdown"
      ) {
        setActivePopup(null);
        setShowInvoiceTable(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activePopup]);

  const openPopup = (formType) => {
    setActivePopup(formType);
  };

  const handleClosePage = () => {
    console.log("Close button clicked.");
  };
  const paymentMethodMap = {
    "Bank Transfer": "bank_transfer",
    Cash: "cash",
    "Credit Card": "credit_card",
  };

  const handleSavePayment = async () => {
    try {
      const selectedInvoiceNumbers = Object.keys(selectedInvoices)
        .filter((id) => selectedInvoices[id])
        .map(
          (id) => invoices.find((inv) => inv.id === parseInt(id))?.invoiceNumber
        )
        .filter(Boolean);

      const paymentPayload = {
        customer_id: selectedCustomerId,
        account: selectedCustomerId,
        amount: parseFloat(totalPayment),
        payment_date: new Date().toISOString().slice(0, 10),
        reference_number: referenceNumber,
        payment_method: paymentMethodMap[paymentMethod],
        invoice_ids: selectedInvoiceNumbers.join(","),
      };

      console.log("Sending paymentPayload:", paymentPayload);

      await axios.post(
        "https://backend.hira-tech.co.uk/order/api/customer-payments/",
        paymentPayload
      );

      // ✅ Loop with correct invoiceNumbers
      for (const id of Object.keys(selectedInvoices)) {
        if (selectedInvoices[id]) {
          const paymentAmount = parseFloat(paymentAmounts[id] || 0);
          const invoiceNumber = invoices.find(
            (inv) => inv.id === parseInt(id)
          )?.invoiceNumber;

          if (paymentAmount > 0 && invoiceNumber) {
            await axios.post(
              "https://backend.hira-tech.co.uk/order/api/update-invoice-payment/",
              {
                invoice_id: invoiceNumber,
                payment_amount: paymentAmount,
              }
            );
          }
        }
      }

      alert("Payment recorded successfully!");
      setActivePopup(null);
      setShowInvoiceTable(false);
    } catch (error) {
      console.error("Error saving payment:", error);
      console.error("Backend says:", error.response?.data);
      alert("Failed to record payment.");
    }
  };

  const [filters, setFilters] = useState({
    account: "",
    type: "All Types",
    startDate: "",
    endDate: "",
  });

  const getFilteredEntries = () => {
    return entries.filter((entry) => {
      // Filter by account
      if (filters.account && entry.account_username !== filters.account) {
        return false;
      }

      // Filter by type (you'll need to adjust this based on your actual data structure)
      if (filters.type !== "All Types") {
        if (filters.type === "Payment" && !entry.payment_date) {
          return false;
        }
        // Add similar checks for other types
      }

      // Filter by date range
      if (filters.startDate || filters.endDate) {
        const entryDate = new Date(entry.payment_date || entry.created_at);
        const startDate = filters.startDate
          ? new Date(filters.startDate)
          : null;
        const endDate = filters.endDate ? new Date(filters.endDate) : null;

        if (startDate && entryDate < startDate) {
          return false;
        }
        if (endDate && entryDate > endDate) {
          return false;
        }
      }

      return true;
    });
  };

  const filteredEntries = getFilteredEntries();
  // Calculate pagination values
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = filteredEntries.slice(0, currentPage * entriesPerPage);
  const totalPages = Math.ceil(filteredEntries.length / entriesPerPage);
  return (
    <div className="relative container mx-auto p-4">
      <button onClick={handleClosePage} className="close-btn" title="Close">
        <FiX size={10} />
      </button>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Posting Entries</h2>
        <div className="flex gap-2 relative" ref={dropdownRef}>
          <button className="filter" onClick={() => openPopup("filter")}>
            <FiFilter />
            Filter
          </button>
          <div className="dropdowns-wrapper relative">
            <button className="create" onClick={() => openPopup("dropdown")}>
              Create New <FiChevronDown />
            </button>
            {activePopup === "dropdown" && (
              <div className="dropdowns-menu absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-10">
                <button
                  className="dropdowns-item block px-4 py-2 hover:bg-gray-100 w-full text-left"
                  onClick={() => openPopup("payment")}
                >
                  {" "}
                  Payment
                </button>
                <button
                  className="dropdowns-item block px-4 py-2 hover:bg-gray-100 w-full text-left"
                  onClick={() => openPopup("invoice")}
                >
                  {" "}
                  Invoice
                </button>
                <button
                  className="dropdowns-item block px-4 py-2 hover:bg-gray-100 w-full text-left"
                  onClick={() => openPopup("journal")}
                >
                  {" "}
                  Credit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      {activePopup && activePopup !== "dropdown" && (
        <div className="popup-overlay">
          <div
            className={`popup-form ${activePopup === "filter" ? "filter" : ""}`}
            ref={popupRef}
          >
            <div className="popup-header bg-blue-600 text-white px-4 py-3 flex justify-between items-center">
              <button
                className="close-btn text-white"
                onClick={() => {
                  setActivePopup(null);
                  setShowInvoiceTable(false);
                }}
              >
                <FiX size={24} />
              </button>
            </div>
            {activePopup === "payment" && (
              <div className="popup-body px-6 py-5 text-base">
                <h3 className="text-xl font-bold">Create New Payment</h3>
                <h4 className="subheading text-lg font-semibold mb-4">
                  Payment Details
                </h4>
                <div className="form-section space-y-6">
                  <div className="form-row flex gap-4">
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Account <span className="required text-red-700">*</span>
                      </label>
                      <select
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        value={selectedAccount}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedAccount(value);
                          const customer = customers.find(
                            (c) => `${c.first_Name} ${c.last_Name}` === value
                          );
                          setSelectedCustomerId(customer ? customer.id : null);
                          if (value !== "Select Account") {
                            setShowInvoiceTable(true);
                          } else {
                            setShowInvoiceTable(false);
                          }
                        }}
                      >
                        <option>Select Account</option>
                        {customers.map((customer) => (
                          <option
                            key={customer.id}
                            value={`${customer.first_Name} ${customer.last_Name}`}
                          >
                            {customer.first_Name} {customer.last_Name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Amount <span className="required text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        value={totalPayment}
                        onChange={(e) =>
                          handleTotalPaymentChange(e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="form-row flex gap-4">
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Payment Date{" "}
                        <span className="required text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        defaultValue="2025-06-14"
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Reference No.
                      </label>
                      <input
                        type="text"
                        value={referenceNumber}
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group w-full">
                      <label className="block text-md font-medium mb-1">
                        Payment Method
                      </label>
                      <select
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      >
                        <option value="">Select Payment Method</option>
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Cash">Cash</option>
                        <option value="Credit Card">Credit Card</option>
                      </select>
                    </div>
                  </div>
                  {showInvoiceTable && (
                    <div
                      style={{
                        maxHeight: "500px",
                        overflowY: "auto",
                        border: "2px solid rgb(207, 203, 203)",
                        borderRadius: "6px",
                        backgroundColor: "white",
                      }}
                    >
                      {" "}
                      <table
                        style={{
                          width: "930px",
                          border: "2px solid rgb(207, 203, 203)",
                          color: "black",
                          backgroundColor: "white",
                        }}
                      >
                        <thead>
                          <tr className="bg-gray-100 text-black">
                            <th className="py-2 px-4 border-b">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  Object.values(selectedInvoices).length ===
                                    invoices.length &&
                                  Object.values(selectedInvoices).every(
                                    (val) => val
                                  )
                                }
                              />
                            </th>
                            <th className="py-2 px-4 border-b">Description</th>
                            <th className="py-2 px-4 border-b">Invoice Date</th>
                            <th className="py-2 px-4 border-b">Total</th>
                            <th className="py-2 px-4 border-b">Remaining</th>
                            <th className="py-2 px-4 border-b">Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <tr
                              key={invoice.id}
                              className="hover:bg-gray-50 text-black"
                            >
                              <td className="py-2 px-4 border-b">
                                <input
                                  type="checkbox"
                                  checked={!!selectedInvoices[invoice.id]}
                                  onChange={() =>
                                    handleCheckboxChange(invoice.id)
                                  }
                                />
                              </td>
                              <td className="py-2 px-4 border-b">
                                {invoice.description}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {invoice.invoiceDate}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {formatPrice(invoice.total)}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {formatPrice(invoice.remaining)}
                              </td>
                              <td className="py-2 px-4 border-b">
                                <input
                                  type="text"
                                  className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                                  value={paymentAmounts[invoice.id] || ""}
                                  onChange={(e) =>
                                    handlePaymentChange(
                                      invoice.id,
                                      e.target.value
                                    )
                                  }
                                  disabled={!selectedInvoices[invoice.id]}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="form-actions flex justify-end gap-4">
                    <button
                      className="btn-cancel"
                      onClick={() => {
                        setActivePopup(null);
                        setShowInvoiceTable(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button className="btn-save" onClick={handleSavePayment}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            {activePopup === "invoice" && (
              <div className="popup-body px-6 py-5 text-base">
                <h3 className="text-xl font-bold">Invoice Details</h3>
                <div className="form-section space-y-6">
                  <div className="form-row flex gap-4">
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Account <span className="required text-red-700">*</span>
                      </label>
                      <select
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        value={selectedAccount}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedAccount(value);
                          const customer = customers.find(
                            (c) => `${c.first_Name} ${c.last_Name}` === value
                          );
                          setSelectedCustomerId(customer ? customer.id : null);
                          if (value !== "Select Account") {
                            setShowInvoiceTable(true);
                          } else {
                            setShowInvoiceTable(false);
                          }
                        }}
                      >
                        <option>Select Account</option>
                        {customers.map((customer) => (
                          <option
                            key={customer.id}
                            value={`${customer.first_Name} ${customer.last_Name}`}
                          >
                            {customer.first_Name} {customer.last_Name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Invoice Date{" "}
                        <span className="required text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        defaultValue="2025-06-14"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group large-textarea flex-1">
                      <label className="block text-md font-medium mb-1">
                        Note <span className="required text-red-500">*</span>
                      </label>
                      <textarea
                        rows="6"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        placeholder="Enter invoice notes here..."
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label
                        style={{
                          fontSize: "20px",
                          marginTop: "20px",
                          marginLeft: "10px",
                        }}
                      >
                        Subtotal {formatPrice(0.98)}
                      </label>
                      <label
                        style={{
                          fontSize: "20px",
                          marginTop: "5px",
                          marginLeft: "10px",
                        }}
                      >
                        Taxes {formatPrice(0.02)}
                      </label>
                      <label
                        style={{
                          fontSize: "20px",
                          marginTop: "5px",
                          marginLeft: "10px",
                        }}
                      >
                        Total {formatPrice(0.00)}
                      </label>
                    </div>
                  </div>
                  <p
                    style={{
                      fontSize: "20px",
                      fontWeight: "430",
                      marginTop: "-20px",
                    }}
                  >
                    This note will be visible to customers
                  </p>
                  {showInvoiceTable && (
                    <div
                      style={{
                        maxHeight: "250px",
                        overflowY: "auto",
                        border: "2px solid rgb(207, 203, 203)",
                        borderRadius: "6px",
                        backgroundColor: "white",
                      }}
                    >
                      {" "}
                      <table
                        style={{
                          width: "930px",
                          border: "2px solid rgb(207, 203, 203)",
                          color: "black",
                          backgroundColor: "white",
                        }}
                      >
                        <thead>
                          <tr className="bg-gray-100 text-black">
                            <th className="py-2 px-4 border-b">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  Object.values(selectedInvoices).length ===
                                    invoices.length &&
                                  Object.values(selectedInvoices).every(
                                    (val) => val
                                  )
                                }
                              />
                            </th>
                            <th className="py-2 px-4 border-b">Description</th>
                            <th className="py-2 px-4 border-b">Invoice Date</th>
                            <th className="py-2 px-4 border-b">Total</th>
                            <th className="py-2 px-4 border-b">Remaining</th>
                            <th className="py-2 px-4 border-b">Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <tr
                              key={invoice.id}
                              className="hover:bg-gray-50 text-black"
                            >
                              <td className="py-2 px-4 border-b">
                                <input
                                  type="checkbox"
                                  checked={!!selectedInvoices[invoice.id]}
                                  onChange={() =>
                                    handleCheckboxChange(invoice.id)
                                  }
                                />
                              </td>
                              <td className="py-2 px-4 border-b">
                                {invoice.description}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {invoice.invoiceDate}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {formatPrice(invoice.total)}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {formatPrice(invoice.remaining)}
                              </td>
                              <td className="py-2 px-4 border-b">
                                <input
                                  type="text"
                                  className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                                  value={paymentAmounts[invoice.id] || ""}
                                  onChange={(e) =>
                                    handlePaymentChange(
                                      invoice.id,
                                      e.target.value
                                    )
                                  }
                                  disabled={!selectedInvoices[invoice.id]}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="form-actions flex justify-end gap-4 mt-6">
                    <button
                      className="btn-cancel"
                      onClick={() => {
                        setActivePopup(null);
                        setShowInvoiceTable(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button className="btn-save">Save</button>
                  </div>
                </div>
              </div>
            )}
            {activePopup === "journal" && (
              <div className="popup-body px-6 py-5 text-base">
                <h3 className="text-xl font-bold">Create New Credit</h3>
                <h4 className="subheading text-lg font-semibold mb-4">
                  Credit Details
                </h4>
                <div className="form-section space-y-6">
                  <div className="form-row flex gap-4">
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Account <span className="required text-red-700">*</span>
                      </label>
                      <select
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        value={selectedAccount}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSelectedAccount(value);
                          const customer = customers.find(
                            (c) => `${c.first_Name} ${c.last_Name}` === value
                          );
                          setSelectedCustomerId(customer ? customer.id : null);
                          if (value !== "Select Account") {
                            setShowInvoiceTable(true);
                          } else {
                            setShowInvoiceTable(false);
                          }
                        }}
                      >
                        <option>Select Account</option>
                        {customers.map((customer) => (
                          <option
                            key={customer.id}
                            value={`${customer.first_Name} ${customer.last_Name}`}
                          >
                            {customer.first_Name} {customer.last_Name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Amount <span className="required text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        value={totalPayment}
                        onChange={(e) =>
                          handleTotalPaymentChange(e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="form-row flex gap-4">
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Credit Date{" "}
                        <span className="required text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        defaultValue="2025-06-14"
                      />
                    </div>
                    <div className="form-group flex-1">
                      <label className="block text-md font-medium mb-1">
                        Reference No.
                      </label>
                      <input
                        type="text"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        placeholder="Enter reference"
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group large-textarea flex-1">
                      <label className="block text-md font-medium mb-1">
                        Description
                      </label>
                      <textarea
                        rows="4"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        placeholder="Enter credit description here..."
                      ></textarea>
                    </div>
                    <div className="form-group">
                      <label style={{ marginTop: "5px" }}>
                        Applied {formatPrice(0.87)}
                      </label>
                      <input
                        type="text"
                        className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                        placeholder="Unapplied"
                      />
                    </div>
                  </div>
                  {showInvoiceTable && (
                    <div
                      style={{
                        maxHeight: "250px",
                        overflowY: "auto",
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        backgroundColor: "white",
                        paddingRight: "4px",
                      }}
                    >
                      <table
                        style={{
                          width: "930px",
                          border: "2px solid rgb(207, 203, 203)",
                          color: "black",
                          backgroundColor: "white",
                        }}
                      >
                        <thead>
                          <tr className="bg-gray-100 text-black">
                            <th className="py-2 px-4 border-b">
                              <input
                                type="checkbox"
                                onChange={handleSelectAll}
                                checked={
                                  Object.values(selectedInvoices).length ===
                                    invoices.length &&
                                  Object.values(selectedInvoices).every(
                                    (val) => val
                                  )
                                }
                              />
                            </th>
                            <th className="py-2 px-4 border-b">Description</th>
                            <th className="py-2 px-4 border-b">Invoice Date</th>
                            <th className="py-2 px-4 border-b">Total</th>
                            <th className="py-2 px-4 border-b">Remaining</th>
                            <th className="py-2 px-4 border-b">Payment</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((invoice) => (
                            <tr
                              key={invoice.id}
                              className="hover:bg-gray-50 text-black"
                            >
                              <td className="py-2 px-4 border-b">
                                <input
                                  type="checkbox"
                                  checked={!!selectedInvoices[invoice.id]}
                                  onChange={() =>
                                    handleCheckboxChange(invoice.id)
                                  }
                                />
                              </td>
                              <td className="py-2 px-4 border-b">
                                {invoice.description}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {invoice.invoiceDate}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {formatPrice(invoice.total)}
                              </td>
                              <td className="py-2 px-4 border-b">
                                {formatPrice(invoice.remaining)}
                              </td>
                              <td className="py-2 px-4 border-b">
                                <input
                                  type="text"
                                  className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                                  value={paymentAmounts[invoice.id] || ""}
                                  onChange={(e) =>
                                    handlePaymentChange(
                                      invoice.id,
                                      e.target.value
                                    )
                                  }
                                  disabled={!selectedInvoices[invoice.id]}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="form-actions flex justify-end gap-4 mt-6">
                    <button
                      style={{
                        marginTop: "350px",
                        color: "white",
                        backgroundColor: "#004aad",
                      }}
                      onClick={() => {
                        setActivePopup(null);
                        setShowInvoiceTable(false);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        marginTop: "350px",
                        color: "white",
                        backgroundColor: "#004aad",
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
            {activePopup === "filter" && (
              <div style={{ marginBottom: "800px", border: "none" }}>
                <h3 style={{ fontSize: "40px" }}>Filter Entries</h3>
                <div className="form-section flex flex-col gap-4">
                  <div className="form-group">
                    <label className="block text-md font-medium mb-1">
                      Account
                    </label>
                    <select
                      className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                      value={filters.account}
                      onChange={(e) =>
                        setFilters({ ...filters, account: e.target.value })
                      }
                    >
                      <option value="">Select Account</option>
                      {customers.map((customer) => (
                        <option
                          key={customer.id}
                          value={`${customer.first_Name} ${customer.last_Name}`}
                        >
                          {customer.first_Name} {customer.last_Name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="block text-md font-medium mb-1">
                      Type
                    </label>
                    <select
                      className="form-input w-full p-2 border border-gray-300 rounded text-lg"
                      value={filters.type}
                      onChange={(e) =>
                        setFilters({ ...filters, type: e.target.value })
                      }
                    >
                      <option>All Types</option>
                      <option>Payment</option>
                      <option>Invoice</option>
                      <option>Credit</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="block text-md font-medium mb-1">
                      Start Date
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        className="form-input flex-1 p-2 border border-gray-300 rounded text-lg"
                        value={filters.startDate}
                        onChange={(e) =>
                          setFilters({ ...filters, startDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="block text-md font-medium mb-1">
                      End Date
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="date"
                        className="form-input flex-1 p-2 border border-gray-300 rounded text-lg"
                        value={filters.endDate}
                        onChange={(e) =>
                          setFilters({ ...filters, endDate: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-actions flex justify-end gap-4 mt-6">
                    <button
                      className="btn-cancel"
                      onClick={() => {
                        setActivePopup(null);
                        setFilters({
                          account: "",
                          type: "All Types",
                          startDate: "",
                          endDate: "",
                        });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn-save"
                      onClick={() => {
                        setActivePopup(null);
                        // Apply filters will happen automatically through the filteredEntries calculation
                      }}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      <p className="text-sm text-gray-600 mb-4">
        Showing {currentEntries.length > 0 ? `${indexOfFirstEntry + 1} to ${Math.min(indexOfLastEntry, filteredEntries.length)}` : "0"} of {filteredEntries.length} entries
      </p>
      <hr className="my-4" />
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 text-black">
          <thead>
            <tr className="bg-gray-100 text-black">
              <th
                className="py-2 px-4 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Post Date
              </th>
              <th
                className="py-2 px-4 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Type
              </th>
              <th
                className="py-2 px-4 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Account
              </th>
              <th
                className="py-2 px-4 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Amount
              </th>
              <th
                className="py-2 px-4 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Reference
              </th>
              <th
                className="py-2 px-4 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Statement Date
              </th>
              <th
                className="py-2 px-5 border-b whitespace-nowrap"
                style={{ textAlign: "left" }}
              >
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {currentEntries.map((entry, index) => (
              <tr key={index} className="hover:bg-gray-50 text-black">
                <td className="py-4 px-4 border-b whitespace-nowrap ">
                  <span className="text-black hover:underline pl-6">
                    {entry.payment_date}
                  </span>
                </td>
                <td className="py-4 px-4 border-b whitespace-nowrap">
                  <span
                    to="/posting-entry"
                    className="text-black hover:underline"
                  >
                    Payment
                  </span>
                </td>
                <td className="py-2 px-4 border-b whitespace-nowrap">
                  <span
                    to="/posting-entry"
                    className="text-black hover:underline"
                  >
                    {entry.account_username}
                  </span>
                </td>
                <td className="py-2 px-4 border-b whitespace-nowrap amount">
  <span to="/posting-entry" className="text-black hover:underline">
    {formatPrice(entry.amount)}
  </span>
</td>
                <td className="py-2 px-4 border-b whitespace-nowrap">
                  {entry.reference_no
                    ? entry.reference_no.split(", ").map((ref, index) => {
                        // Find the invoice that matches this reference number
                        const matchedInvoice = entry.invoices.find(
                          (inv) => inv.invoice_number === ref.trim()
                        );

                        const orderId =
                          matchedInvoice?.order_details?.order_Id || ref.trim();

                        return (
                          <span key={index}>
                            {index > 0 && ", "}
                            <NavLink
                              to={`/admin/invoice-detail/${orderId}`}
                              className="text-black hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {ref.trim()}
                            </NavLink>
                          </span>
                        );
                      })
                    : "—"}
                </td>
                <td className="py-2 px-4 border-b whitespace-nowrap ">
                  <span
                    to="/posting-entry"
                    className="text-black hover:underline"
                  >
                    {entry.created_at.split("T")[0]}
                  </span>
                </td>
                <td className="py-2 px-4 border-b whitespace-nowrap">
                  <button
                    onClick={() => handleDelete(entry)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <FaTrashAlt
                      className="inline-block"
                      style={{ color: "#dc2626", fontSize: "18px" }}
                    />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredEntries.length > entriesPerPage && currentPage < totalPages && (
          <div className="flex justify-center mt-4">
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              style={{ backgroundColor: "#004aad" , marginTop: "20px",marginLeft: "1250px" }}
            >
              Show more entries
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PostingEntries;